/**
 * Client-side mirror of server/utils/plans.ts
 * Used by the auth store and UI components for plan feature checks.
 * Keep in sync with the server config.
 */

export type PlanName = 'free' | 'pro' | 'elite'

export const PLAN_LIMITS = {
  free: { clips: 0, audio: 1, avatars: 2, backgrounds: 1, cursors: 0, customLinks: 0, customFonts: 0, friends: 10 },
  pro: { clips: 6, audio: 3, avatars: 10, backgrounds: 5, cursors: 3, customLinks: 3, customFonts: 0, friends: 25 },
  elite: { clips: 18, audio: 6, avatars: 25, backgrounds: 15, cursors: 10, customLinks: Infinity, customFonts: 5, friends: Infinity }
} as const

export type LimitKey = keyof typeof PLAN_LIMITS.free

export const PLAN_FEATURES = {
  free: {
    layouts: ['default', 'compact'],
    effects: ['none'],
    cursorTrails: ['none'],
    fonts: ['Rajdhani', 'Space Grotesk', 'Play'],
    iconStyles: ['default', 'bordered', 'glow'],
    // Array order matters: [0] is the read-time enforcement fallback
    linkStyles: ['minimal', 'outline'],
    avatarShapes: ['circle'],
    avatarBorders: ['none'],
    cardAnimations: ['none'],
    cardHovers: ['none'],
    cardBorderStyles: ['subtle', 'solid', 'none'],
    separatorStyles: ['gradient', 'solid', 'none'],
    connections: ['discord'],
    richTextBio: false,
    sectionReordering: true,
    displayName: false,
    showAsEmbed: false,
    customEnterLabel: false,
    customSectionTitles: false,
    videoBackgrounds: false,
    backgroundGradient: false,
    comments: false,
    branding: true
  },
  pro: {
    layouts: ['default', 'compact', 'split'],
    effects: ['none', 'rain', 'snow', 'particles', 'stars', 'fire', 'fireflies', 'smoke'],
    cursorTrails: ['none', 'sparkles', 'glow', 'smoke'],
    fonts: ['Rajdhani', 'Space Grotesk', 'Play', 'Orbitron', 'Audiowide', 'Chakra Petch', 'Russo One', 'Teko'],
    iconStyles: ['default', 'bordered', 'glow', 'neon', 'bounce', 'pulse'],
    linkStyles: ['icon', 'minimal', 'outline', 'card', 'pill'],
    avatarShapes: ['circle', 'rounded'],
    avatarBorders: ['none', 'ring', 'glow'],
    cardAnimations: ['none', 'fadeIn', 'fadeUp', 'scaleIn', 'slideLeft'],
    cardHovers: ['none', 'lift', 'scale', 'glow'],
    cardBorderStyles: ['subtle', 'solid', 'glow', 'none'],
    separatorStyles: ['gradient', 'solid', 'dashed', 'glow', 'none'],
    connections: ['discord', 'youtube', 'twitch'],
    richTextBio: true,
    sectionReordering: true,
    displayName: true,
    showAsEmbed: false,
    customEnterLabel: true,
    customSectionTitles: true,
    videoBackgrounds: true,
    backgroundGradient: true,
    comments: true,
    branding: false
  },
  elite: {
    layouts: ['default', 'compact', 'split', 'grid'],
    effects: ['none', 'rain', 'snow', 'particles', 'stars', 'fire', 'matrix', 'fireflies', 'smoke', 'glitch', 'sparks', 'sakura', 'plasma', 'lasers'],
    cursorTrails: ['none', 'sparkles', 'glow', 'smoke', 'embers', 'bubbles', 'rainbow', 'magic', 'neon', 'hearts', 'lightning'],
    fonts: ['Rajdhani', 'Space Grotesk', 'Play', 'Orbitron', 'Audiowide', 'Chakra Petch', 'Russo One', 'Teko'],
    iconStyles: ['default', 'bordered', 'glow', 'neon', 'bounce', 'pulse', 'wave', 'spin', 'rainbow', 'glitch', 'shake', 'flicker'],
    linkStyles: ['icon', 'minimal', 'outline', 'card', 'pill'],
    avatarShapes: ['circle', 'rounded', 'hexagon'],
    avatarBorders: ['none', 'ring', 'glow'],
    cardAnimations: ['none', 'fadeIn', 'fadeUp', 'scaleIn', 'slideLeft', 'slideRight'],
    cardHovers: ['none', 'lift', 'scale', 'glow', 'tilt'],
    cardBorderStyles: ['subtle', 'solid', 'glow', 'none'],
    separatorStyles: ['gradient', 'solid', 'dashed', 'glow', 'none'],
    connections: ['discord', 'youtube', 'twitch'],
    richTextBio: true,
    sectionReordering: true,
    displayName: true,
    showAsEmbed: true,
    customEnterLabel: true,
    customSectionTitles: true,
    videoBackgrounds: true,
    backgroundGradient: true,
    comments: true,
    branding: false
  }
} as const

// ---------------------------------------------------------------------------
// Add-ons - standalone monthly modules, independent of the plan tier.
// Client mirror of server/utils/addons.ts (ADDONS).
// ---------------------------------------------------------------------------

export type AddonKey = 'custom_domain'

export interface AddonConfig {
  name: string
  tagline: string
  icon: string
  monthlyEur: number
  features: string[]
  setupPath: string
  setupLabel: string
}

export const ADDONS: Record<AddonKey, AddonConfig> = {
  custom_domain: {
    name: 'Custom Domain',
    tagline: 'Point a domain you own at your page, so visitors see your domain instead of mychar.gg.',
    icon: 'i-lucide-globe',
    monthlyEur: 2.99,
    features: [
      'Works with any domain you own',
      'Automatic SSL, live within minutes',
      'Your mychar.gg link keeps working',
      'Available on every plan',
      'Cancel anytime'
    ],
    setupPath: '/dashboard/settings/domain',
    setupLabel: 'Set up your domain'
  }
}

/** Returns the minimum plan required for a given feature value. */
export function getRequiredPlan(feature: string, value: string): PlanName | null {
  // Custom fonts use format "custom:<uuid>" and require elite plan
  if (feature === 'fonts' && value?.startsWith('custom:')) {
    return 'elite'
  }
  const tiers: PlanName[] = ['free', 'pro', 'elite']
  for (const tier of tiers) {
    const featureConfig = PLAN_FEATURES[tier]
    const allowed = featureConfig[feature as keyof typeof featureConfig]
    if (Array.isArray(allowed) && (allowed as readonly string[]).includes(value)) {
      return tier
    }
  }
  return null
}
